/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode, useCallback, useMemo, useRef, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {ScheduledReport} from '@ion/core/src/types/entities';
import {ConfirmationPrompt} from '@ion/dls/src/editors/ConfirmationPrompt';
import EditorDialog from '@ion/dls/src/editors/EditorDialog';
import ScheduledReportEditor from '@ion/dls/src/editors/ScheduledReportEditor';
import useScheduledReportMutation from '@ion/query/src/hooks/useScheduledReportMutation';
import withSignals from '../../components/hoc/withSignals';
import {
  createScheduledReport,
  duplicatingScheduledReport,
  editingScheduledReport,
  editScheduledReport,
  selectedDashboardReport,
  setCreateScheduledReport,
  setDuplicatingScheduledReport,
  setEditingScheduledReport,
  setEditScheduledReport,
  setShowAddScheduledReportEditor,
  showAddScheduledReportEditor
} from './store';

/**
 * @ScheduledReportEditorController
 */
export interface ScheduledReportEditorControllerProps {
  onRefresh?: () => void;
  onUpdateRow?: (item: ScheduledReport, versionCheck?: Boolean) => void;
  onShowSnackbar?: (
    message?: ReactNode,
    type?: string,
    options?: {showScheduledReportLink?: boolean}
  ) => void;
}

export const ScheduledReportEditorController = ({
  onRefresh, onUpdateRow, onShowSnackbar
}: ScheduledReportEditorControllerProps) => {
  const scheduledReport = editingScheduledReport.value ? editScheduledReport.value : createScheduledReport.value;
  
  const submitRef                               = useRef<any>(null);
  const entityLabel                             = translate('sisense:formLabels.title');
  const [showSnackbar, setShowSnackbar]         = useState(false);
  const [snackbarMessage, setSnackbarMessage]   = useState('');
  const [snackbarType, setSnackbarType]         = useState<'success' | 'error'>('success');
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);
  const dialogTitle                             = useMemo(() => {
    const titleLabel = translate('sisense:analytics.topNavigation.tabs.scheduledReports');
    if (duplicatingScheduledReport.value) {
      return `${translate('sisense:analytics.menu.duplicate')} ${titleLabel}`;
    }
    return translate(
      editingScheduledReport.value ? 'common:editors.shared.edit' : 'common:editors.shared.add',
      {entityLabel: titleLabel}
    );
  }, [duplicatingScheduledReport.value, editingScheduledReport.value]);

  const closeSnackbar = useCallback(() => {
    setShowSnackbar(false);
  }, []);

  const closeCancelPrompt = useCallback(() => {
    setShowCancelPrompt(false);
  }, []);

  const confirmCancel = useCallback(() => {
    setShowCancelPrompt(false);
    closeEditor();
  }, [closeEditor]);

  const closeEditor = useCallback(() => {
    setCreateScheduledReport(null);
    setDuplicatingScheduledReport(false);
    setEditingScheduledReport(false);
    setShowAddScheduledReportEditor(false);
    setEditScheduledReport(null);
  }, []);

  const onCancel = useCallback(() => {
    if (isLoading) return;
    setShowCancelPrompt(true);
  }, [isLoading]);

  const getSuccessMessage = useCallback((response) => (
    response?.data?.message ||
    response?.message ||
    translate(
      editingScheduledReport.value
        ? 'common:editors.shared.successEdited'
        : 'common:editors.shared.successCreated',
      {entity: entityLabel}
    )
  ), [entityLabel]);

  const getErrorMessage = useCallback((error) => (
    error?.response?.data?.response?.errors?.[0]?.error?.errors?.[0] ||
    error?.response?.data?.response?.errors?.[0]?.error?.description ||
    error?.response?.data?.message ||
    error?.message ||
    translate(
      editingScheduledReport.value
        ? 'common:editors.shared.errorEdited'
        : 'common:editors.shared.errorCreated',
      {entity: entityLabel}
    )
  ), [entityLabel]);

  const {
    save,
    isLoading
  } = useScheduledReportMutation(editingScheduledReport.value ? 'edit' : 'create',
    async(response) => {
      if (editingScheduledReport.value) {
        // Editing an existing report: update the row in place instead of refreshing the grid.
        onUpdateRow?.(response);
        setSnackbarType('success');
        setSnackbarMessage(getSuccessMessage(response));
        setShowSnackbar(true);
      } else {
        // Creating a new report: refresh the grid to load it in.
        await onRefresh?.();
        onShowSnackbar(getSuccessMessage(response), 'success', {showScheduledReportLink: true});
      }
      closeEditor();
    }, (error) => {
      setSnackbarType('error');
      setSnackbarMessage(getErrorMessage(error));
      setShowSnackbar(true);
    }
  );

  const onSave = useCallback((serialized) => {
    const {account, ...rest} = serialized;
    const payload = {
      ...rest,
      emails      : serialized.emails,
      description : serialized.description || ''
    };

    if (editingScheduledReport.value) {
      payload.id = rest?.id;
    }

    save(payload);
  }, [save]);

  const onPrimaryButtonClick = useCallback((e) => submitRef.current?.(e), []);

  const cancelPromptTitle = useMemo(() => {
    if (duplicatingScheduledReport.value) {
      return translate('sisense:analytics.cancelPrompt.title.duplicate');
    }
    return translate(
      `sisense:analytics.cancelPrompt.title.${editingScheduledReport.value ? 'edit' : 'add'}`
    );
  }, [duplicatingScheduledReport.value, editingScheduledReport.value]);

  return (
    <>
      <EditorDialog open={showAddScheduledReportEditor.value}
        dialogTitle={dialogTitle}
        onCloseDialog={onCancel as any}
        onCancelDialog={onCancel as any}
        onPrimaryButtonClick={onPrimaryButtonClick}
        primaryButtonLoading={isLoading}
        omitActions={false}>
        <ScheduledReportEditor onCancel={onCancel}
          onSave={onSave}
          submitRef={submitRef}
          scheduledReport={scheduledReport}
          selectedDashboardReport={selectedDashboardReport?.value} />
      </EditorDialog>
      <ConfirmationPrompt show={showCancelPrompt}
        title={cancelPromptTitle}
        message={translate('sisense:analytics.cancelPrompt.message')}
        primaryActionLabel={translate('sisense:analytics.cancelPrompt.confirm')}
        onPrimaryAction={confirmCancel}
        secondaryActionlabel={translate('common:editors.delete.cancel')}
        onSecondaryAction={closeCancelPrompt}
        isSuccess={showSnackbar && snackbarType === 'success'}
        successMessage={snackbarType === 'success' ? snackbarMessage : ''}
        isFailure={showSnackbar && snackbarType === 'error'}
        failureMessage={snackbarType === 'error' ? snackbarMessage : ''}
        snackbarType={snackbarType}
        snackbarIsIcon={snackbarType === 'error'}
        snackbarMaxWidth='640px'
        onMessageClose={closeSnackbar} />
    </>
  );
};

export default withSignals(ScheduledReportEditorController);
